import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getKinnserRescrapeStatus,
  pullKinnser,
  rescrapeKinnser,
} from '../../services/api';
import type { KinnserPullSummary } from '../../services/api/types.gen';
import { formatDateTimeLocal } from '../../utils/dateUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

/**
 * Pulls an agency's clinic info from Kinnser and saves it on its demo config.
 *
 * A pull writes only the config; the next run writes it to the agency's tables
 * and never calls Kinnser itself. Both the settings dialog and the run dialog
 * need this.
 */
export function usePullKinnser() {
  const [pulling, setPulling] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [result, setResult] = useState<KinnserPullSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped by reset, so a request still out for an agency left behind writes
  // nothing to the pane that has moved on.
  const generation = useRef(0);

  // The new summary, or null when the pull failed.
  const pull = useCallback(
    async (agencyId: string): Promise<KinnserPullSummary | null> => {
      const mine = generation.current;
      const current = () => generation.current === mine;
      setPulling(true);
      setError(null);
      try {
        const res = await pullKinnser({
          path: { agency_id: agencyId },
          throwOnError: true,
        });
        if (!current()) return null;
        setResult(res.data ?? null);
        return res.data ?? null;
      } catch (caught) {
        console.error('Failed to pull from Kinnser', caught);
        if (current()) {
          setError(
            getBusinessErrorMessage(caught) ??
              'Unable to pull from Kinnser. Please try again.'
          );
        }
        return null;
      } finally {
        if (current()) setPulling(false);
      }
    },
    []
  );

  // Asks RogerDoc to scrape Kinnser again, waits for it, then pulls as usual.
  const rescrapeAndPull = useCallback(
    async (agencyId: string): Promise<KinnserPullSummary | null> => {
      const mine = ++generation.current;
      const current = () => generation.current === mine;
      setScraping(true);
      setError(null);
      try {
        const started = await rescrapeKinnser({
          path: { agency_id: agencyId },
          throwOnError: true,
        });
        const after = started.data.after;
        const deadline = Date.now() + RESCRAPE_TIMEOUT_MS;
        let pollErrors = 0;
        for (;;) {
          await new Promise(resolve => setTimeout(resolve, RESCRAPE_POLL_MS));
          if (!current()) return null;
          let status: string | null = null;
          try {
            const res = await getKinnserRescrapeStatus({
              path: { agency_id: agencyId },
              query: { after },
              throwOnError: true,
            });
            status = res.data.status;
            pollErrors = 0;
          } catch (caught) {
            // One bad poll is not a failed scrape; RogerDoc keeps scraping.
            if (++pollErrors >= RESCRAPE_MAX_POLL_ERRORS) throw caught;
            console.warn('Kinnser rescrape status poll failed', caught);
          }
          if (!current()) return null;
          if (status === 'completed') break;
          if (status === 'failed') {
            setError(RESCRAPE_FAILED);
            return null;
          }
          if (Date.now() > deadline) {
            setError(RESCRAPE_STILL_RUNNING);
            return null;
          }
        }
      } catch (caught) {
        console.error('Failed to rescrape Kinnser', caught);
        if (current()) {
          setError(getBusinessErrorMessage(caught) ?? RESCRAPE_FAILED);
        }
        return null;
      } finally {
        if (current()) setScraping(false);
      }
      return pull(agencyId);
    },
    [pull]
  );

  // A pane that unmounts mid-scrape stops polling and never pulls.
  useEffect(
    () => () => {
      generation.current++;
    },
    []
  );

  const reset = useCallback(() => {
    generation.current++;
    setPulling(false);
    setScraping(false);
    setResult(null);
    setError(null);
  }, []);

  return { pull, pulling, rescrapeAndPull, scraping, result, error, reset };
}

const RESCRAPE_POLL_MS = 5_000;
const RESCRAPE_TIMEOUT_MS = 10 * 60_000;
const RESCRAPE_MAX_POLL_ERRORS = 3;
const RESCRAPE_FAILED = 'Kinnser scrape failed. Please try again.';
const RESCRAPE_STILL_RUNNING =
  'Kinnser is still being scraped. Try Pull again in a few minutes.';

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** "Pulled 9/11/2026, 10:42 AM: 1 clinic, 29 insurances, 885 physicians, 40 staff." */
export function formatPullSummary(pull: KinnserPullSummary): string {
  return `Pulled ${formatDateTimeLocal(pull.pulled_at)}: ${plural(
    pull.clinics,
    'clinic'
  )}, ${plural(pull.insurances, 'insurance')}, ${plural(
    pull.physicians,
    'physician'
  )}, ${pull.staff ?? 0} staff.`;
}
